import type {
  Configuration,
  SurfaceTypesMapValue,
  SurfaceCategoriesMapValue,
  SurfaceCollectionsMapValue,
  SurfacesMapValue,
  SurfaceMaps,
  SurfaceParents,
  SurfaceCollectionParents,
  SurfaceCategoryParents,
  SurfaceOptionGroups,
  SurfaceOptionsGroup,
} from '~/types/configurator'

function turnSurfacesIntoMap(
  surfaces: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces']
) {
  return surfaces.reduce((acc, surface) => {
    acc.set(surface.nid, surface)
    return acc
  }, new Map<number, SurfacesMapValue>())
}

function turnSurfaceCollectionIntoMap(
  collection: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections']
) {
  return collection.reduce((acc, collection) => {
    acc.set(collection.nid, {
      ...collection,
      surfaces: turnSurfacesIntoMap(collection.surfaces),
    } satisfies SurfaceCollectionsMapValue)
    return acc
  }, new Map<number, SurfaceCollectionsMapValue>())
}

function turnSurfaceCategoryIntoMap(
  categories: Configuration['surfaceTypes'][number]['surfaceCategories']
) {
  return categories.reduce((acc, category) => {
    acc.set(category.nid, {
      ...category,
      surfaceCollections: turnSurfaceCollectionIntoMap(
        category.surfaceCollections
      ),
    } satisfies SurfaceCategoriesMapValue)
    return acc
  }, new Map<number, SurfaceCategoriesMapValue>())
}

function turnSurfaceTypesIntoMap(surfaceTypes: Configuration['surfaceTypes']) {
  return surfaceTypes.reduce((acc, type) => {
    acc.set(type.nid, {
      ...type,
      surfaceCategories: turnSurfaceCategoryIntoMap(type.surfaceCategories),
    } satisfies SurfaceTypesMapValue)
    return acc
  }, new Map<number, SurfaceTypesMapValue>())
}

export default function useConfigurationSurfaces(
  configuration: Ref<Configuration | null> = ref(null),
  values: Ref<Record<string, Configuration['values'][number]>>,
  multiselectableAccessories: Ref<Record<string, Configuration['values']>>,
  currentValues: ComputedRef<Configuration['values']>
) {
  const surfaceTypes = computed(() => {
    if (!configuration.value) {
      return new Map<number, SurfaceTypesMapValue>()
    }

    return turnSurfaceTypesIntoMap(configuration.value.surfaceTypes)
  })

  const surfaceCategoriesMap = computed(() => {
    if (!configuration.value) {
      return new Map<number, SurfaceCategoryParents>()
    }

    return [...surfaceTypes.value.values()].reduce((acc, type) => {
      type.surfaceCategories.forEach((category) => {
        acc.set(category.nid, {
          typeNid: type.nid,
        })
      })
      return acc
    }, new Map<number, SurfaceCategoryParents>())
  })

  const surfaceCollectionsMap = computed(() => {
    if (!configuration.value) {
      return new Map<number, SurfaceCollectionParents>()
    }

    return [...surfaceTypes.value.values()].reduce((acc, type) => {
      type.surfaceCategories.forEach((category) => {
        category.surfaceCollections.forEach((collection) => {
          acc.set(collection.nid, {
            typeNid: type.nid,
            categoryNid: category.nid,
          })
        })
      })
      return acc
    }, new Map<number, SurfaceCollectionParents>())
  })

  const surfacesMap = computed(() => {
    if (!configuration.value) {
      return new Map<number, SurfaceParents>()
    }
    return [...surfaceTypes.value.values()].reduce((acc, type) => {
      type.surfaceCategories.forEach((category) => {
        category.surfaceCollections.forEach((collection) => {
          collection.surfaces.forEach((surface) => {
            acc.set(surface.nid, {
              typeNid: type.nid,
              categoryNid: category.nid,
              collectionNid: collection.nid,
            })
          })
        })
      })
      return acc
    }, new Map<number, SurfaceParents>())
  })

  const surfaceMaps = computed(() => {
    if (!configuration.value) {
      return {
        surfaceCategories: new Map<number, SurfaceCategoryParents>(),
        surfaceCollections: new Map<number, SurfaceCollectionParents>(),
        surfaces: new Map<number, SurfaceParents>(),
      } satisfies SurfaceMaps
    }

    return {
      surfaceCategories: surfaceCategoriesMap.value,
      surfaceCollections: surfaceCollectionsMap.value,
      surfaces: surfacesMap.value,
    } satisfies SurfaceMaps
  })

  const surfaceOptionsGroups = computed(() => {
    if (!configuration.value) {
      return new Map<string, SurfaceOptionsGroup>()
    }

    return configuration.value.surfacesOptions.reduce((acc, option) => {
      if (values.value[option.partCategoryNid]?.basePartDeselected) {
        // if optional base part is deselected, don't show the option
        return acc
      }

      if (option.surfaceCollectionNids.length === 0) {
        // if the option has no surface collection, don't show the option
        return acc
      }

      let optionAdded = false

      if (option.surfacesInheritedFromProduct) {
        // base parts that inherit surface collections from product can be grouped by icon (surface type icon)
        const collectionNid = option.surfaceCollectionNids[0]
        const { type } = configuratorUtils.findSurfaceCollectionByNid(
          surfaceTypes.value,
          surfaceMaps.value,
          collectionNid
        )

        if (type && type.icon?.fileName) {
          const key = type.icon.fileName
          const value = acc.get(key)
          if (!value) {
            acc.set(key, {
              icon: type.icon,
              surfaceOptionGroups: new Map<number, SurfaceOptionGroups>().set(
                type.nid,
                {
                  surfaceType: {
                    nid: type.nid,
                    title: type.title,
                  },
                  surfaceOptions: [option],
                } satisfies SurfaceOptionGroups
              ),
            })
          } else {
            // if the icon already exists, add the option to the existing group
            const surfaceOptionGroup = value.surfaceOptionGroups.get(type.nid)
            if (surfaceOptionGroup) {
              surfaceOptionGroup.surfaceOptions.push(option)
            } else {
              value.surfaceOptionGroups.set(type.nid, {
                surfaceType: {
                  nid: type.nid,
                  title: type.title,
                },
                surfaceOptions: [option],
              } satisfies SurfaceOptionGroups)
            }
          }
          optionAdded = true
        }
      }

      if (!optionAdded) {
        // cannot be grouped by icon (accessories or base parts with custom surface collections or no surface type icon)
        const key = 'no-icon-' + option.partCategoryNid
        acc.set(key, {
          icon: undefined,
          surfaceOptionGroups: new Map<number, SurfaceOptionGroups>().set(
            option.partCategoryNid,
            {
              surfaceType: undefined,
              surfaceOptions: [option],
            } satisfies SurfaceOptionGroups
          ),
        })
      }
      return acc
    }, new Map<string, SurfaceOptionsGroup>())
  })

  const surfaceCategoryMismatch = computed(() => {
    if (!configuration.value) {
      return new Map<string, Map<number, boolean>>()
    }

    return [...surfaceOptionsGroups.value.entries()].reduce(
      (acc, [key, surfaceOptionsGroup]) => {
        return acc.set(
          key,
          [...surfaceOptionsGroup.surfaceOptionGroups.entries()].reduce(
            (acc, [nid, value]) => {
              if (
                value.surfaceOptions.length === 1 ||
                value.surfaceOptions.length === 0
              ) {
                // only one option or no options
                acc.set(nid, false)
              } else {
                const categories = value.surfaceOptions
                  .map(
                    (option) =>
                      configuratorUtils.findValue(
                        currentValues.value,
                        option.partCategoryNid,
                        option.accessoryNid
                      )?.surfaceNid
                  )
                  .filter(
                    (surfaceNid) =>
                      surfaceNid !== undefined && surfaceNid !== null
                  )
                  .map(
                    (surfaceNid) =>
                      configuratorUtils.findSurfaceByNid(
                        surfaceTypes.value,
                        surfaceMaps.value,
                        surfaceNid
                      ).category?.nid
                  )

                acc.set(nid, !categories.every((val) => val === categories[0]))
              }
              return acc
            },
            new Map<number, boolean>()
          )
        )
      },
      new Map<string, Map<number, boolean>>()
    )
  })

  const globalSurfaceChanges = ref(new Map<string, Map<number, boolean>>())
  const activeSurfaceOption = ref<[string, number] | undefined>(undefined)
  const chosenParts = ref(new Set<[number, number | null | undefined]>())

  watch(
    () => surfaceOptionsGroups.value,
    () => {
      globalSurfaceChanges.value.clear()

      surfaceOptionsGroups.value.forEach((surfaceOptionsGroup, key) => {
        globalSurfaceChanges.value.set(
          key,
          [...surfaceOptionsGroup.surfaceOptionGroups.entries()].reduce(
            (acc, [key, surfaceOptions]) => {
              if (!surfaceOptions.surfaceType) {
                // options are not grouped by type so global surface change is always true
                return acc.set(key, true)
              } else {
                // check if all surface options have the same surface = global surface change is true
                // if not, global surface change is false
                const surfaceNids = surfaceOptions.surfaceOptions
                  .map(
                    (option) =>
                      configuratorUtils.findValue(
                        currentValues.value,
                        option.partCategoryNid,
                        option.accessoryNid
                      )?.surfaceNid
                  )
                  .filter(
                    (surfaceNid) =>
                      surfaceNid !== undefined && surfaceNid !== null
                  )
                return acc.set(
                  key,
                  surfaceNids.every((val) => val === surfaceNids[0])
                )
              }
            },
            new Map<number, boolean>()
          )
        )
      })
    },
    { immediate: true, deep: true }
  )

  function setChosenParts(
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    partCategoryNid?: number | null,
    accessoryNid?: number | null
  ) {
    activeSurfaceOption.value = [
      surfaceOptionsGroupsKey,
      surfaceOptionGroupsKey,
    ]

    chosenParts.value.clear()
    if (partCategoryNid) {
      chosenParts.value.add([partCategoryNid, accessoryNid])
    } else {
      surfaceOptionsGroups.value
        .get(surfaceOptionsGroupsKey)
        ?.surfaceOptionGroups.get(surfaceOptionGroupsKey)
        ?.surfaceOptions.forEach((option) => {
          chosenParts.value.add([option.partCategoryNid, option.accessoryNid])
        })
    }
  }

  function updateChosenPartsSurfaces(
    surfaceNid?: number | null,
    surfaceTranslationOfNid?: number | null,
    surfaceImageSrc?: string | null,
    surfaceTitle?: string | null
  ) {
    chosenParts.value.forEach(([partCategoryNid, accessoryNid]) => {
      if (accessoryNid && multiselectableAccessories.value[partCategoryNid]) {
        multiselectableAccessories.value[partCategoryNid].forEach((value) => {
          if (value.partNid === accessoryNid) {
            value.surfaceNid = surfaceNid
            value.surfaceTranslationOfNid = surfaceTranslationOfNid
            value.surfaceImageSrc = surfaceImageSrc
            value.surfaceTitle = surfaceTitle
          }
        })
      } else {
        if (!values.value[partCategoryNid]) return

        values.value[partCategoryNid].surfaceNid = surfaceNid
        values.value[partCategoryNid].surfaceTranslationOfNid =
          surfaceTranslationOfNid
        values.value[partCategoryNid].surfaceImageSrc = surfaceImageSrc
        values.value[partCategoryNid].surfaceTitle = surfaceTitle
      }
    })
  }

  function setGlobalSurfaceChange(
    surfaceOptionsGroupsKey: string,
    surfaceOptionGroupsKey: number,
    globalSurfaceChange: boolean,
    value?: Configuration['values'][number]
  ) {
    globalSurfaceChanges.value
      .get(surfaceOptionsGroupsKey)
      ?.set(surfaceOptionGroupsKey, globalSurfaceChange)

    if (globalSurfaceChange && value) {
      setChosenParts(surfaceOptionsGroupsKey, surfaceOptionGroupsKey)
      updateChosenPartsSurfaces(
        value.surfaceNid,
        value.surfaceTranslationOfNid,
        value.surfaceImageSrc,
        value.surfaceTitle
      )
    }
  }

  return {
    globalSurfaceChanges,
    activeSurfaceOption,
    chosenParts,
    surfaceCategoryMismatch,
    surfaceOptionsGroups,
    surfaceTypes,
    surfaceMaps,
    setChosenParts,
    updateChosenPartsSurfaces,
    setGlobalSurfaceChange,
  }
}
